import React from "react";
import { createPortal } from "react-dom";
import { Icon } from "../icons.jsx";

/* ============================================================
   Talention — shared UI helpers
   ============================================================ */

const Card = ({ title, action, eyebrow, children, className = "", style }) => (
  <div className={"card " + className} style={style}>
    {(title || action || eyebrow) && (
      <div className="card-header">
        <div>
          {eyebrow && <div className="card-eyebrow">{eyebrow}</div>}
          {title && <div className="card-title">{title}</div>}
        </div>
        {action}
      </div>
    )}
    {children}
  </div>
);

const Button = ({ variant = "secondary", size, icon, children, onClick, type, style, disabled, title }) => (
  <button
    type={type || "button"}
    className={"btn btn-" + variant + (size ? " btn-" + size : "")}
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={disabled ? { opacity: 0.45, cursor: "not-allowed", ...style } : style}
  >
    {icon && <Icon name={icon} size={size === "sm" ? 13 : 15} />}
    {children}
  </button>
);

const Badge = ({ tone = "", dot, children, style }) => (
  <span className={"badge " + tone} style={style}>
    {dot && <span style={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor", display: "inline-block", flexShrink: 0 }} />}
    {children}
  </span>
);

/* Escala de un puesto. `manual` marca las fijadas a mano como excepción
   al cálculo automático, para que nunca se confundan con las calculadas. */
const ScaleBadge = ({ scale, manual = false, reason = "", showLabel = false }) => {
  if (!scale) return <span className="muted">—</span>;
  return (
    <span
      className="badge"
      style={{
        background: scale.color + "22",
        color: scale.color,
        border: manual ? `1px dashed ${scale.color}80` : undefined,
      }}
      title={manual ? `Escala fijada a mano${reason ? ": " + reason : ""}` : `Rango ${scale.min} – ${scale.max} pts`}
    >
      {manual && <Icon name="pin" size={11} />}
      {scale.name}{showLabel && scale.label ? ` · ${scale.label}` : ""}
    </span>
  );
};

/* Clasificación oficial de un puesto, sea cual sea el método: la escala en
   Talention (con su marca de fijada a mano) o la agrupación en Ministerio.
   Recibe la `Clasificacion` de data.js (clasificacionDelPuesto/DePuntos). */
const ClasificacionBadge = ({ clasificacion, showLabel = false }) => {
  if (!clasificacion) return <span className="muted">—</span>;
  const { etiqueta, detalle, color, rango, manual, motivo } = clasificacion;
  return (
    <span
      className="badge"
      style={{
        background: color + "22",
        color,
        border: manual ? `1px dashed ${color}80` : undefined,
      }}
      title={manual ? `Escala fijada a mano${motivo ? ": " + motivo : ""}` : rango}
    >
      {manual && <Icon name="pin" size={11} />}
      {etiqueta}{showLabel && detalle ? ` · ${detalle}` : ""}
    </span>
  );
};

/* Aviso de incoherencia entre la jerarquía y la puntuación de un puesto. */
const ReviewBadge = ({ review, compact = false }) => {
  if (!review) return null;
  return (
    <span
      className="badge warn"
      style={{ whiteSpace: "nowrap" }}
      title={`Pendiente de revisión: ${review.message}`}
    >
      <Icon name="alert" size={11} />
      {compact ? "Revisar" : "Pendiente de revisión"}
    </span>
  );
};

const Field = ({ label, hint, children }) => (
  <label className="field">
    {label && <span className="field-label">{label}</span>}
    {children}
    {hint && <span className="field-hint">{hint}</span>}
  </label>
);

const Check = ({ checked, onChange }) => (
  <button
    type="button"
    className={"check " + (checked ? "on" : "")}
    onClick={() => onChange(!checked)}
    aria-pressed={checked}
  />
);

const Radio = ({ checked, onChange }) => (
  <button
    type="button"
    className={"radio " + (checked ? "on" : "")}
    onClick={onChange}
    aria-pressed={checked}
  />
);

const Switch = ({ on, onChange }) => (
  <button
    type="button"
    className={"switch " + (on ? "on" : "")}
    onClick={() => onChange(!on)}
  />
);

const Segmented = ({ value, options, onChange }) => (
  <div className="segmented">
    {options.map(o => (
      <button
        key={o.value}
        className={value === o.value ? "active" : ""}
        onClick={() => onChange(o.value)}
      >{o.label}</button>
    ))}
  </div>
);

/* El modal se monta en document.body, fuera del árbol del apartado: la capa
   lleva `svpt-root` para seguir dentro del ámbito de su CSS. */
const Modal = ({ open, onClose, title, children, footer, width = 520 }) => {
  if (!open) return null;
  return createPortal(
    <div
      className="svpt-root"
      style={{
        position: "fixed", inset: 0, background: "rgba(15,18,26,0.34)",
        backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
        zIndex: 100, display: "grid", placeItems: "center", padding: 20,
      }}
      onClick={onClose}
    >
      <div
        className="card fade-in"
        style={{
          width: "100%", maxWidth: width, maxHeight: "calc(100dvh - 40px)",
          padding: 0, boxShadow: "var(--shadow-lg)", overflow: "hidden",
          display: "flex", flexDirection: "column", minHeight: 0,
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "18px 22px", borderBottom: "1px solid var(--border)", flexShrink: 0,
        }}>
          <div style={{ fontWeight: 700, fontSize: 16, letterSpacing: "-0.02em" }}>{title}</div>
          <button className="icon-btn" onClick={onClose} style={{ width: 28, height: 28 }}>
            <Icon name="x" size={14} />
          </button>
        </div>
        <div style={{ padding: 22, overflowY: "auto", flex: "1 1 auto", minHeight: 0 }}>{children}</div>
        {footer && (
          <div style={{
            display: "flex", justifyContent: "flex-end", gap: 8,
            padding: "14px 22px", borderTop: "1px solid var(--border)", background: "var(--bg-soft)", flexShrink: 0,
          }}>{footer}</div>
        )}
      </div>
    </div>,
    document.body
  );
};

/* Donut chart (SVG) */
const Donut = ({ value = 0, size = 140, stroke = 14, color = "var(--brand-500)", track = "var(--ink-150)", children }) => {
  const r = (size - stroke) / 2;
  const C = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  const offset = C - (pct / 100) * C;
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={r} stroke={track} strokeWidth={stroke} fill="none" />
        <circle
          cx={size/2} cy={size/2} r={r}
          stroke={color} strokeWidth={stroke} fill="none"
          strokeDasharray={C} strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size/2} ${size/2})`}
          style={{ transition: "stroke-dashoffset 400ms cubic-bezier(.2,.7,.2,1)" }}
        />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", textAlign: "center" }}>
        {children}
      </div>
    </div>
  );
};

/* Multi-arc donut */
const MultiDonut = ({ segments, size = 180, stroke = 18, gap = 0.02, children }) => {
  const r = (size - stroke) / 2;
  const C = 2 * Math.PI * r;
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  let cursor = 0;
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={r} stroke="var(--ink-150)" strokeWidth={stroke} fill="none" />
        {segments.map((seg, i) => {
          const frac = seg.value / total;
          const len = frac * C - gap * C;
          const off = -cursor * C;
          cursor += frac;
          return (
            <circle
              key={i}
              cx={size/2} cy={size/2} r={r}
              stroke={seg.color} strokeWidth={stroke} fill="none"
              strokeDasharray={`${Math.max(0, len)} ${C}`}
              strokeDashoffset={off}
              strokeLinecap="butt"
              transform={`rotate(-90 ${size/2} ${size/2})`}
              style={{ transition: "all 400ms" }}
            />
          );
        })}
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", textAlign: "center" }}>
        {children}
      </div>
    </div>
  );
};

/* Sparkline bar chart */
const BarChart = ({ data, height = 120, color = "var(--brand-500)", labelKey = "label", valueKey = "value" }) => {
  const max = Math.max(1, ...data.map(d => d[valueKey]));
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 10, height, padding: "4px 0" }}>
      {data.map((d, i) => {
        const h = (d[valueKey] / max) * (height - 28);
        return (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--ink-600)" }}>{d[valueKey]}</div>
            <div style={{
              width: "100%", height: h, minHeight: 4,
              background: d.color || color,
              borderRadius: "8px 8px 4px 4px",
              transition: "height 400ms cubic-bezier(.2,.7,.2,1)",
            }} />
            <div style={{ fontSize: 11, color: "var(--ink-500)", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{d[labelKey]}</div>
          </div>
        );
      })}
    </div>
  );
};

/* Slider with current value chip */
const Slider = ({ value, onChange, min = 0, max = 100, step = 1, color = "var(--brand-500)" }) => {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="slider-track" style={{ position: "relative" }}>
      <div style={{
        position: "absolute", left: 0, right: 0, height: 6,
        background: "var(--ink-150)", borderRadius: 999, top: "50%", transform: "translateY(-50%)",
      }} />
      <div style={{
        position: "absolute", left: 0, width: pct + "%", height: 6,
        background: color, borderRadius: 999, top: "50%", transform: "translateY(-50%)",
        transition: "width 120ms ease",
      }} />
      <input
        type="range"
        className="range"
        min={min} max={max} step={step}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        style={{ background: "transparent", position: "relative", zIndex: 3 }}
      />
    </div>
  );
};

export { Card, Button, Badge, ScaleBadge, ClasificacionBadge, ReviewBadge, Field, Check, Radio, Switch, Segmented, Modal, Donut, MultiDonut, BarChart, Slider };
